import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productAPI } from '../../services/api';
import ProductList from '../products/ProductList';
import toast from 'react-hot-toast';

const AdminProductList = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 20
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, [filters]);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await productAPI.getAllProducts(filters);
      
      setProducts(response.data.content || []);
      setPagination({
        page: response.data.number || 0,
        totalPages: response.data.totalPages || 0,
        totalElements: response.data.totalElements || 0,
        size: response.data.size || 20
      });
    } catch (error) {
      console.error('Failed to load products:', error);
      setError('Failed to load products. Please try again.');
      setProducts([]);
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductEdit = (productId) => {
    navigate(`/admin/products/edit/${productId}`);
  };

  const handleProductDelete = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.name}"? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await productAPI.deleteProduct(productId);
        toast.success('Product deleted successfully');
        loadProducts(); // Reload the list
      } catch (error) {
        console.error('Failed to delete product:', error);
        toast.error('Failed to delete product. Please try again.');
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/admin/products/create');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 0 : value // Reset to first page when changing other filters
    }));
  };

  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  };

  const titleStyle = {
    margin: 0,
    color: '#333',
    fontSize: '28px'
  };

  const createButtonStyle = {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s'
  };

  const filtersStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const selectStyle = {
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#495057'
  };

  const statsStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '20px'
  };

  const statCardStyle = {
    backgroundColor: '#fff',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    textAlign: 'center'
  };

  const statValueStyle = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2c5aa0',
    margin: '0 0 4px 0'
  };

  const statLabelStyle = {
    fontSize: '14px',
    color: '#6c757d',
    margin: 0
  };

  const paginationStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '30px'
  };

  const pageButtonStyle = {
    padding: '8px 12px',
    border: '1px solid #dee2e6',
    backgroundColor: '#fff',
    color: '#495057',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const activePageButtonStyle = {
    ...pageButtonStyle,
    backgroundColor: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  };

  // Calculate stats
  const activeProducts = products.filter(p => p.isActive !== false).length;
  const inactiveProducts = products.filter(p => p.isActive === false).length;
  const lowStockProducts = products.filter(p => p.stockQuantity < 10).length;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Product Management</h1>
        <button
          onClick={handleCreateNew}
          style={createButtonStyle}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
        >
          ＋ Add New Product
        </button>
      </div>

      {/* Statistics */}
      <div style={statsStyle}>
        <div style={statCardStyle}>
          <p style={statValueStyle}>{pagination.totalElements}</p>
          <p style={statLabelStyle}>Total Products</p>
        </div>
        <div style={statCardStyle}>
          <p style={statValueStyle}>{activeProducts}</p>
          <p style={statLabelStyle}>Active Products</p>
        </div>
        <div style={statCardStyle}>
          <p style={statValueStyle}>{inactiveProducts}</p>
          <p style={statLabelStyle}>Inactive Products</p>
        </div>
        <div style={statCardStyle}>
          <p style={statValueStyle}>{lowStockProducts}</p>
          <p style={statLabelStyle}>Low Stock (＜10)</p>
        </div>
      </div>

      {/* Filters */}
      <div style={filtersStyle}>
        <div>
          <label style={labelStyle}>Sort By:</label>
          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            style={selectStyle}
          >
            <option value="name">Name</option>
            <option value="price">Price</option>
            <option value="stockQuantity">Stock</option>
            <option value="createdAt">Date Created</option>
            <option value="updatedAt">Last Updated</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Direction:</label>
          <select
            value={filters.sortDirection}
            onChange={(e) => handleFilterChange('sortDirection', e.target.value)}
            style={selectStyle}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Per Page:</label>
          <select
            value={filters.size}
            onChange={(e) => handleFilterChange('size', parseInt(e.target.value))}
            style={selectStyle}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Product List */}
      <ProductList
        products={products}
        isLoading={isLoading}
        isAdminView={true}
        onProductEdit={handleProductEdit}
        onProductDelete={handleProductDelete}
      />

      {/* Pagination */}
      {!isLoading && !error && pagination.totalPages > 1 && (
        <div style={paginationStyle}>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 0}
            style={{
              ...pageButtonStyle,
              opacity: pagination.page <= 0 ? 0.5 : 1,
              cursor: pagination.page <= 0 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Previous
          </button>

          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            let pageNum;
            if (pagination.totalPages <= 5) {
              pageNum = i;
            } else if (pagination.page < 3) {
              pageNum = i;
            } else if (pagination.page >= pagination.totalPages - 3) {
              pageNum = pagination.totalPages - 5 + i;
            } else {
              pageNum = pagination.page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={
                  pageNum === pagination.page 
                    ? activePageButtonStyle 
                    : pageButtonStyle
                }
              >
                {pageNum + 1}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages - 1}
            style={{
              ...pageButtonStyle,
              opacity: pagination.page >= pagination.totalPages - 1 ? 0.5 : 1,
              cursor: pagination.page >= pagination.totalPages - 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminProductList;